/* eslint-disable indent */
import { html, css, nothing, LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers/connect-mixin.js';
import { store } from '../../store/store.js';
import sharedStyles from '../../app/styles.js';
import {
    exportAccreditationInfos,
    loadAccreditationInfosIfNeeded,
    loadOrganizationOptionsIfNeeded,
} from '../../store/accreditationInfo/accreditationInfo.js';
import { searchCatalogItems, clearSearchResults } from '../../store/catalog/catalog.js';
import { navigate } from '../../store/route/route.js';
import { translate } from '../../helpers/translate.js';
import { formatDate, getExpirationStatus } from '../../helpers/controls.js';
import { comboBoxRenderer } from '@vaadin/combo-box/lit.js';
import { Debouncer } from '@polymer/polymer/lib/utils/debounce.js';
import { timeOut } from '@polymer/polymer/lib/utils/async.js';
import '@lit-labs/virtualizer';
import '@sdb-develop/learning-frontend';
import '@vaadin/select/theme/material/vaadin-select.js';
import '@vaadin/combo-box/theme/material/vaadin-combo-box.js';
import '@polymer/paper-toggle-button/paper-toggle-button.js';
import '@material/web/fab/fab.js';
import '../shared/sdb-filter-button.js';
import '../shared/sdb-search-input.js';
import '../shared/sdb-side-sheet.js';
import '../shared/sdb-loading.js';
import '../shared/sdb-status-message.js';
import '../shared/sdb-dialog.js';

const DEFAULT_SORT = 'Expiration';
// vaadin-select turns an item without a value into its label, so "all" gets a sentinel.
const ALL = 'all';

@customElement('sdb-accreditation-info-list')
class SdbAccreditationInfoList extends connect(store)(LitElement) {

    @state()
    accessor accreditationInfos;

    @state()
    accessor total;

    @state()
    accessor search;

    @state()
    accessor sort;

    @state()
    accessor showExpired;

    @state()
    accessor organization;

    @state()
    accessor organizationOptions = [];

    @state()
    accessor filtersOpened = false;

    @state()
    accessor isLoadingFirstTime;

    @state()
    accessor catalogItems;

    @state()
    accessor isCatalogLoading;

    @state()
    accessor selectedCatalogItem;

    @state()
    accessor reviewPortal;

    @state()
    accessor exportStatus;

    @state()
    accessor exportError;

    stateChanged(state) {
        this.accreditationInfos = state.accreditationInfo.list;
        this.total = state.accreditationInfo.total;
        this.search = state.accreditationInfo.search;
        this.sort = state.accreditationInfo.sort;
        this.showExpired = state.accreditationInfo.showExpired;
        this.organization = state.accreditationInfo.organization;
        this.organizationOptions = state.accreditationInfo.organizationOptions;
        this.isLoadingFirstTime = state.accreditationInfo.listStatus === 'loading' && state.accreditationInfo.page === -1;
        this.catalogItems = state.catalog.list;
        this.isCatalogLoading = state.catalog.status === 'loading';
        this.reviewPortal = state.env.settings?.accreditationProviderPortal;
        this.exportStatus = state.accreditationInfo.exportStatus;
        this.exportError = state.accreditationInfo.exportError;
    }

    static styles = [
        sharedStyles,
        css`
            :host {
                max-width: var(--tcg-content-max-width);
                width: 100%;
            }

            #accreditationinfos_list {
                contain: none !important; /* lit-virtualizer hack */
            }

            sdb-content-listitem {
                width: 100%; /* lit-virtualizer screws this up */
            }

            .toolbar {
                display: flex;
                align-items: center;
                gap: 15px;
            }

            .toolbar sdb-search-input {
                flex: 1;
                min-width: 0;
            }

            sdb-side-sheet paper-toggle-button {
                margin-top: 16px;
            }
        `,
    ];

    get activeFilterCount() {
        return [
            (this.sort || DEFAULT_SORT) !== DEFAULT_SORT,
            Boolean(this.organization),
            Boolean(this.showExpired),
        ].filter(Boolean).length;
    }

    render() {
        if (this.isLoadingFirstTime) {
            return html`<sdb-loading></sdb-loading>`;
        }

        return html`
            <sdb-layout-50-50>
                <sdb-nav-titlebar slot="left">
                    <span>${translate('headerAccreditationInfos')}</span>
                    <span ?hidden=${this.total === undefined}>&nbsp;(${this.total})</span>
                </sdb-nav-titlebar>
                <div slot="right" class="toolbar">
                    <sdb-search-input
                        placeholder=${translate('search')}
                        value=${this.search}
                        @search=${(e) => { store.dispatch(loadAccreditationInfosIfNeeded({
                            search: e.detail.searchText,
                            reload: true
                        })); }}>
                    </sdb-search-input>
                    <sdb-filter-button
                        label=${translate('filters')}
                        count=${this.activeFilterCount}
                        @click=${() => { this._openFilters(); }}>
                    </sdb-filter-button>
                    <sdb-content-iconbutton
                        icon="fal fa-file-excel"
                        title=${translate('exportExcel')}
                        aria-label=${translate('exportExcel')}
                        ?disabled=${!this.total || this.exportStatus === 'loading'}
                        @click=${() => { store.dispatch(exportAccreditationInfos()); }}>
                    </sdb-content-iconbutton>
                </div>
            </sdb-layout-50-50>

            <sdb-side-sheet
                heading=${translate('filters')}
                ?opened=${this.filtersOpened}
                @close=${() => { this.filtersOpened = false; }}>
                <vaadin-select
                    label=${translate('sortBy')}
                    value=${this.sort}
                    .items=${[
                        { label: translate('sortName'), value: 'Name' },
                        { label: translate('sortExpiration'), value: 'Expiration' },
                        { label: translate('sortLastModified'), value: 'LastModified' },
                    ]}
                    @change=${(e) => { this._filter({ sort: e.target.value }); }}>
                </vaadin-select>
                <vaadin-select
                    label=${translate('pe.organization')}
                    value=${this.organization || ALL}
                    .items=${[{ value: ALL, label: translate('allOrganizations') }, ...this.organizationOptions]}
                    @change=${(e) => { this._filter({ organization: e.target.value === ALL ? '' : e.target.value }); }}>
                </vaadin-select>
                <paper-toggle-button
                    ?checked=${this.showExpired}
                    @change=${(e) => { this._filter({ showExpired: e.target.checked }); }}>
                    <span>${translate('showExpired')}</span>
                </paper-toggle-button>

                <sdb-content-button
                    slot="actions"
                    label=${translate('resetFilters')}
                    ?disabled=${this.activeFilterCount === 0}
                    @click=${() => { this._filter({ sort: DEFAULT_SORT, organization: '', showExpired: false }); }}>
                </sdb-content-button>
            </sdb-side-sheet>

            <sdb-layout-100>
                ${!this.total
                    ? html`<sdb-status-message label=${translate('noAccreditationInfoFound')}></sdb-status-message>`
                    : html`
                        <sdb-content-list separator>
                            <lit-virtualizer
                                id="accreditationinfos_list"
                                .items=${this.accreditationInfos}
                                .renderItem=${(accreditationInfo, index) => this._renderListItem(accreditationInfo, index)}
                                .scrollTarget=${document}>
                            </lit-virtualizer>
                        </sdb-content-list>
                    `
                }
                <md-fab @click=${() => { this._openAddDialog(); }}>
                    <sdb-content-icon
                        slot="icon"
                        icon="fal fa-plus"
                        size="24">
                    </sdb-content-icon>
                </md-fab>
            </sdb-layout-100>

            <sdb-dialog
                type="info"
                heading=${translate('exportFailed')}
                body=${this.exportError ?? ''}
                .opened=${this.exportStatus === 'failed'}>
            </sdb-dialog>

            <sdb-dialog
                type="form"
                id="add_dialog"
                heading=${translate('addAccreditationInfoHeading')}
                confirmCaption=${translate('select')}
                @confirm=${() => { this._confirmAdd(); }}
                .bodyRenderer=${() => html`
                    <vaadin-combo-box
                        id="catalog_item_combobox"
                        style="width: 500px;"
                        label=${translate('searchCatalogItem')}
                        helper-text=${translate('helper_min2chars')}
                        item-id-path="targetId"
                        item-label-path="name"
                        ?loading=${this.isCatalogLoading}
                        .filteredItems=${this.catalogItems}
                        @filter-changed=${(e) => this._onCatalogFilterChanged(e)}
                        @selected-item-changed=${(e) => { this.selectedCatalogItem = e.target.selectedItem; }}
                        ${comboBoxRenderer((item) => this._catalogItemRenderer(item), [])}>
                    </vaadin-combo-box>
                `}>
            </sdb-dialog>
        `;
    }

    _openAddDialog() {
        this.selectedCatalogItem = null;
        store.dispatch(clearSearchResults());
        this.renderRoot.querySelector('#add_dialog').open();
    }

    _onCatalogFilterChanged(e) {
        this._onCatalogFilterChangedDebouncer = Debouncer.debounce(
            this._onCatalogFilterChangedDebouncer,
            timeOut.after(400),
            () => {
                if (!e.detail.value || e.detail.value.length < 2) {
                    store.dispatch(clearSearchResults());
                    return;
                }

                store.dispatch(searchCatalogItems({
                    search: e.detail.value,
                    portals: [this.reviewPortal],
                }));
            }
        );
    }

    _catalogItemRenderer(item) {
        return html`
            <div class="combo-item">
                <div class="label">${item.name}</div>
                <div
                    class="subtext"
                    ?hidden=${!item.summary}
                    style="overflow: hidden; font-weight: 300; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;">
                    ${item.summary}
                </div>
            </div>
        `;
    }

    _filter(changes) {
        store.dispatch(loadAccreditationInfosIfNeeded({ ...changes, reload: true }));
    }

    _openFilters() {
        store.dispatch(loadOrganizationOptionsIfNeeded());
        this.filtersOpened = true;
    }

    _confirmAdd() {
        if (this.selectedCatalogItem) {
            store.dispatch(navigate(`composition/${this.selectedCatalogItem.targetId}/accreditationinfo`));
        }
    }

    _renderListItem(accreditationInfo, index) {
        store.dispatch(loadAccreditationInfosIfNeeded({ index }));

        const mappings = accreditationInfo.mappings ?? accreditationInfo.accreditations;
        const lastValidTill = this._getLastValidTill(mappings);

        return html`
            <sdb-content-listitem
                icon="fal fa-file-certificate"
                statusColor=${this._getWarningColor(lastValidTill)}
                .headline=${accreditationInfo.courseName}
                .subtitle=${this._renderSubtitle(accreditationInfo)}
                @click=${() => {
                    store.dispatch(navigate(`composition/${accreditationInfo.targetId}/accreditationinfo`));
                }}>
                ${this._renderOrganizationChips(mappings)}
            </sdb-content-listitem>
        `;
    }

    // One chip per professional body the course has mappings for.
    _renderOrganizationChips(mappings) {
        const organizations = [...new Set((mappings ?? []).map((mapping) => mapping.organizationKey).filter(Boolean))]
            .sort((a, b) => a.localeCompare(b));

        if (organizations.length === 0) {
            return nothing;
        }

        return html`
            <sdb-content-chipbar slot="chipbar">
                ${organizations.map((organization) => html`<sdb-content-chip label=${organization}></sdb-content-chip>`)}
            </sdb-content-chipbar>
        `;
    }

    _getWarningColor(date) {
        switch (getExpirationStatus(date, 3, 'months')) {
        case 'expired': return 'red';
        case 'almost-expired': return 'orange';
        default: return '';
        }
    }

    _renderSubtitle(accreditationInfo) {
        const lastValidTill = this._getLastValidTill(accreditationInfo.mappings ?? accreditationInfo.accreditations);

        return [translate(accreditationInfo.targetType), lastValidTill ? `${translate('expiresAt')} ${formatDate(lastValidTill)}` : null]
            .filter(Boolean)
            .join(' · ');
    }

    _getLastValidTill(mappings) {
        return (mappings ?? [])
            .map((mapping) => mapping.validTill)
            .filter(Boolean)
            .reduce((latest, date) => (!latest || new Date(date) > new Date(latest) ? date : latest), null);
    }
}
